import type { CreateEventHeaderLayoutSettings } from '@graphql/generated-base';
import { ThemeVariationType, DesignLayoutType } from '@graphql/generated-base';
import { deriveDesignTemplateId } from './useDesignTemplates.utils';
import { useCallback } from 'react';
import { getHeaderPhotoSchemaConfig } from '@apps/admin/common/websiteHeaderSchema/useHeaderPhotoPresentationData.utils';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { v4 as uuidv4 } from 'uuid';
import { FULL_CROP_INFO } from './useDesignTemplates.constants';
import type { HorizontalAlignment, VerticalAlignment } from '@shared/types/WebsiteHeaderSettings.types';

const DEFAULT_FULL_WIDTH_IMAGE_URL = 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-rustic_chic-fullWidth.png';

type DesignTemplateHeaderData = Readonly<{
  variationType: ThemeVariationType;
  designThemeId: string;
  photoPresentationLayout: PhotoPresentationLayout;
  photos?: {
    url: string;
    height: number;
    width: number;
  }[];
  jsonContainerId?: string;
  colors?: {
    nameAndNavFontColor: string;
    nameAndNavBackgroundColor: string;
    nameAndNavBackgroundColorEnabled: boolean;
  };
  eventName?: {
    textAlignment: HorizontalAlignment;
    pageAlignment: {
      x: HorizontalAlignment;
      y: VerticalAlignment;
    };
  };
}>;

/**
 * Utility function to create a fullWidth template configuration
 * @param themeId - The theme identifier for deriveDesignTemplateId
 * @param imageUrl - Optional URL of the image (defaults to standard unsplash image)
 * @returns DesignTemplateHeaderData object for fullWidth layout
 */
const createFullWidthTemplate = (themeId: string, imageUrl: string = DEFAULT_FULL_WIDTH_IMAGE_URL): DesignTemplateHeaderData => ({
  variationType: ThemeVariationType.brannan,
  designThemeId: deriveDesignTemplateId(themeId, 'fullWidth'),
  photoPresentationLayout: 'fullWidth',
  photos: [
    {
      url: imageUrl,
      height: 829,
      width: 1920,
    },
  ],
});

// This data will be moved to server.
export const DESIGN_TEMPLATES_HEADER_DATA: ReadonlyArray<DesignTemplateHeaderData> = [
  createFullWidthTemplate('charlotte'),
  createFullWidthTemplate('coastal'),
  createFullWidthTemplate('editorial_lime'),
  createFullWidthTemplate('emilio_mocha'),
  createFullWidthTemplate('gala_black'),
  createFullWidthTemplate('magazine_indigo'),
  createFullWidthTemplate('minimalist_arch'),
  createFullWidthTemplate('modern_arch'),
  createFullWidthTemplate('modern_cursive_light'),
  createFullWidthTemplate('newsstand_olive'),
  createFullWidthTemplate('newsstand_taffy'),
  createFullWidthTemplate('queens_palace'),
  createFullWidthTemplate('regal_garden'),
  createFullWidthTemplate('rose_bed'),
  createFullWidthTemplate('rustic_minimal'),
  createFullWidthTemplate('terra_iii'),
  createFullWidthTemplate('tiffany_lilac'),
  createFullWidthTemplate('tiffany_red_ii'),
  createFullWidthTemplate('tiffany_red'),
  createFullWidthTemplate('wavy_pink'),
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('nelson', 'photoDuoRight'),
    photoPresentationLayout: 'photoDuoRight',
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/nelson-duo_right1.png',
        width: 814,
        height: 994,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/nelson-duo_right2.png',
        width: 1476,
        height: 994,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('minimalist_arch_mocha', 'photoArch'),
    photoPresentationLayout: 'photoArch',
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/minimalist_arch_mocha-arch.png',
        width: 1202,
        height: 994,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('magazine', 'photoTrio'),
    photoPresentationLayout: 'photoTrio',
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/magazine-trio1.png',
        width: 763,
        height: 994,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/magazine-trio2.png',
        width: 763,
        height: 994,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/magazine-trio3.png',
        width: 763,
        height: 994,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('lydia', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#FFFFFF',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-lydia-photoBackground.png',
        width: 1483,
        height: 926,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('chloe', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#919478',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-chloe-photoBackground.png',
        width: 1248,
        height: 832,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('pomona', 'photoMarquee'),
    photoPresentationLayout: 'photoMarquee',
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/pomona-marquee1.png',
        width: 1424,
        height: 615,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/pomona-marquee2.png',
        width: 1424,
        height: 615,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/pomona-marquee3.png',
        width: 1424,
        height: 615,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('rosebed', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#FFFFFF',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-rosebed-photoBackground.png',
        width: 1423,
        height: 949,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('tourmaline', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#D4FF67',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-tourmaline-photoBackground.png',
        width: 1423,
        height: 949,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('felicity_olive', 'monogram'),
    photoPresentationLayout: 'monogram',
    jsonContainerId: '2d4ab4ec-104d-4bd7-b758-f64e26df64e2',
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('classical_crest_navy', 'monogram'),
    photoPresentationLayout: 'monogram',
    jsonContainerId: '7644f8e5-83d0-4857-9860-22ef936d03c6',
  },
  createFullWidthTemplate('quince', 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/quince-fullWidth.png'),
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('stone_and_gold', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#6C6C6C',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-stone_and_gold-photoBackground.png',
        width: 1423,
        height: 949,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('stamp_papaya', 'monogram'),
    photoPresentationLayout: 'monogram',
    jsonContainerId: '3cdbf20e-6735-4073-8b78-b32429b51a62',
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('mountain_getaway', 'photoBackground'),
    photoPresentationLayout: 'photoBackground',
    colors: {
      nameAndNavFontColor: '#445959',
      nameAndNavBackgroundColor: '#FFFFFF',
      nameAndNavBackgroundColorEnabled: false,
    },
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/brannan-mountain_getaway-photoBackground.png',
        width: 1423,
        height: 949,
      },
    ],
  },
  {
    variationType: ThemeVariationType.brannan,
    designThemeId: deriveDesignTemplateId('mulberry', 'photoSlider'),
    photoPresentationLayout: 'photoSlider',
    photos: [
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/mulberry-slider1.png',
        width: 1424,
        height: 615,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/mulberry-slider2.png',
        width: 1424,
        height: 615,
      },
      {
        url: 'https://withjoy.com/assets/public/wedding-website/designs-gallery/default-assets/mulberry-slider3.png',
        width: 1424,
        height: 615,
      },
    ],
  },
];

/**
 * @returns a function that returns the template header data for the given design theme id and variation type
 */
export const useTemplateHeaderData = () => {
  const getTemplateHeaderData = useCallback((designThemeId: string, variationType: ThemeVariationType): CreateEventHeaderLayoutSettings | null => {
    const templateHeaderData = DESIGN_TEMPLATES_HEADER_DATA.find(template => template.variationType === variationType && template.designThemeId === designThemeId);

    if (!templateHeaderData) {
      console.warn(`Template header data not found for design theme id: ${designThemeId} and variation type: ${variationType}`);
      return null;
    }

    // Get the schema configuration for the photo presentation layout
    const layout: DesignLayoutType = variationType === ThemeVariationType.brannan ? DesignLayoutType.brannan : DesignLayoutType.aloha;
    const config = getHeaderPhotoSchemaConfig(layout, templateHeaderData.photoPresentationLayout);

    if (!config) {
      return null;
    }

    const isMonogramLayout = templateHeaderData.photoPresentationLayout === 'monogram';
    // Create the dataJSON with the photos from our template data
    // For monogram layouts, don't include photos as they're text-based designs
    const dataJSON = {
      ...config.template,
      ...(isMonogramLayout && templateHeaderData.jsonContainerId ? { jsonContainerId: templateHeaderData.jsonContainerId } : {}),
      ...(!isMonogramLayout && templateHeaderData.photos
        ? {
            photos: templateHeaderData.photos.map(photo => ({
              id: uuidv4(),
              url: photo.url,
              height: photo.height,
              width: photo.width,
              cropInfo: FULL_CROP_INFO,
            })),
          }
        : {}),
      ...(templateHeaderData.photoPresentationLayout === 'photoBackground'
        ? {
            eventName: {
              content: '',
              hasModifiedEventNameOnce: false,
              textAlignment: 'center',
              pageAlignment: {
                x: 'center',
                y: 'center',
              },
            },
          }
        : {}),
      ...(templateHeaderData.colors ? { colors: templateHeaderData.colors } : {}),
    };

    return {
      schemaURL: config.schemaUrl,
      dataJSON: dataJSON,
      websiteLayout: layout,
    };
  }, []);

  return { getTemplateHeaderData };
};
